'use client';

import React, { useMemo } from 'react';
import * as d3 from 'd3';

export interface HorizontalBarDatum {
  label: string;
  value: number;
  color: string;
}

export interface MedHorizontalBarChartProps {
  data: HorizontalBarDatum[];
  maxItems?: number;
  formatValue?: (value: number) => string;
}

const BAR_HEIGHT = 24;
const BAR_MIN_PCT = 2;

const defaultFormatValue = (value: number) => Number(value).toLocaleString();

const MedHorizontalBarChart: React.FC<MedHorizontalBarChartProps> = ({
  data,
  maxItems = 5,
  formatValue = defaultFormatValue,
}) => {
  const items = useMemo(
    () => [...data].sort((a, b) => b.value - a.value).slice(0, maxItems),
    [data, maxItems]
  );

  const maxValue = useMemo(() => d3.max(items, (d) => d.value) ?? 0, [items]);

  const scale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, maxValue || 1])
        .range([BAR_MIN_PCT, 100]),
    [maxValue]
  );

  if (items.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-1.5 w-full"
      role="img"
      aria-label="Horizontal bar chart"
      data-testid="med-horizontal-bar-chart"
    >
      {items.map((item, index) => (
        <div
          key={`${item.label}-${index}`}
          className="flex items-center gap-2"
          role="listitem"
          aria-label={`${item.label}: ${formatValue(item.value)}`}
          data-testid={`bar-row-${index}`}
        >
          <span
            className="text-xs text-secondary truncate shrink-0 w-32"
            title={item.label}
            aria-hidden="true"
          >
            {item.label}
          </span>
          <div className="flex-1 min-w-0">
            <svg
              className="w-full overflow-visible"
              height={BAR_HEIGHT}
              aria-hidden="true"
            >
              <rect
                width={`${scale(item.value)}%`}
                height={BAR_HEIGHT}
                rx={3}
                fill={item.color}
                className="transition-opacity duration-200 hover:opacity-80"
                data-testid={`bar-rect-${index}`}
              />
            </svg>
          </div>
          <span
            className="text-xs font-semibold text-primary shrink-0 w-16 text-right tabular-nums"
            aria-hidden="true"
          >
            {formatValue(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default MedHorizontalBarChart;
